/**
 * Tool Call Aggregator
 *
 * Aggregates multiple stream chunks by toolCallId into a single AggregatedToolCall.
 * This is critical for proper tool rendering - a single tool invocation may generate
 * multiple stream chunks (input-start, input-delta, input-available, multiple
 * output-available with preliminary=true, final output-available).
 *
 * Source: PRD-TOOL-COMPONENTS-REFACTOR.md Section 6
 */

import type {
  AggregatedToolCall,
  UIToolInvocationState,
  ToolOutputEntry,
} from '../types/tools';

/**
 * Message part type - simplified for aggregation purposes
 */
interface MessagePart {
  type: string;
  toolCallId?: string;
  toolName?: string;
  title?: string;
  input?: unknown;
  inputTextDelta?: string;
  output?: unknown;
  preliminary?: boolean;
  errorText?: string;
  approval?: {
    id: string;
    approved?: boolean;
    reason?: string;
  };
  state?: string;
}

/**
 * Create a new empty aggregated tool call
 */
function createEmptyAggregatedToolCall(toolCallId: string): AggregatedToolCall {
  return {
    toolCallId,
    toolName: '',
    inputChunks: [],
    input: undefined,
    inputComplete: false,
    outputs: [],
    finalOutput: undefined,
    state: 'input-streaming',
  };
}

/**
 * Aggregate message parts into tool calls grouped by toolCallId
 *
 * This function processes all parts in a message and groups tool-related
 * chunks into AggregatedToolCall objects that contain the full state of
 * each tool invocation.
 *
 * @param parts - Array of message parts from UIMessage
 * @returns Map of toolCallId to AggregatedToolCall
 */
export function aggregateToolCalls(parts: MessagePart[]): Map<string, AggregatedToolCall> {
  const toolCalls = new Map<string, AggregatedToolCall>();

  for (const part of parts) {
    // Skip non-tool parts
    if (!part.type?.startsWith('tool-')) continue;

    const toolCallId = part.toolCallId;
    if (!toolCallId) continue;

    // Get or create aggregated state
    let agg = toolCalls.get(toolCallId);
    if (!agg) {
      agg = createEmptyAggregatedToolCall(toolCallId);
      toolCalls.set(toolCallId, agg);
    }

    // Handle legacy tool-* format (e.g., tool-searchCandidates)
    // These parts have a different structure with state property
    if (part.state) {
      handleLegacyToolPart(agg, part);
      continue;
    }

    // Handle AI SDK v6 chunk types
    switch (part.type) {
      case 'tool-input-start':
        handleInputStart(agg, part);
        break;

      case 'tool-input-delta':
        handleInputDelta(agg, part);
        break;

      case 'tool-input-available':
        handleInputAvailable(agg, part);
        break;

      case 'tool-output-available':
        handleOutputAvailable(agg, part);
        break;

      case 'tool-output-error':
        handleOutputError(agg, part);
        break;

      case 'tool-output-denied':
        handleOutputDenied(agg);
        break;

      case 'tool-approval-requested':
        handleApprovalRequested(agg, part);
        break;

      case 'tool-approval-responded':
        handleApprovalResponded(agg, part);
        break;

      default:
        // Unknown tool part type - may be a custom tool type (e.g., tool-searchCandidates)
        handleUnknownToolPart(agg, part);
    }
  }

  return toolCalls;
}

/**
 * Handle tool-input-start chunk
 */
function handleInputStart(agg: AggregatedToolCall, part: MessagePart): void {
  if (part.toolName) {
    agg.toolName = part.toolName;
  }
  agg.title = part.title;
  agg.state = 'input-streaming';
}

/**
 * Handle tool-input-delta chunk
 */
function handleInputDelta(agg: AggregatedToolCall, part: MessagePart): void {
  if (part.inputTextDelta) {
    agg.inputChunks.push(part.inputTextDelta);
  }
}

/**
 * Handle tool-input-available chunk
 */
function handleInputAvailable(agg: AggregatedToolCall, part: MessagePart): void {
  agg.input = part.input;
  agg.inputComplete = true;
  if (part.toolName) {
    agg.toolName = part.toolName;
  }
  agg.title = part.title ?? agg.title;
  agg.state = 'input-available';
}

/**
 * Handle tool-output-available chunk
 */
function handleOutputAvailable(agg: AggregatedToolCall, part: MessagePart): void {
  const output = part.output;
  const preliminary = part.preliminary ?? false;

  const outputEntry: ToolOutputEntry = {
    output,
    preliminary,
    timestamp: Date.now(),
  };

  agg.outputs.push(outputEntry);

  if (!preliminary) {
    agg.finalOutput = output;
  }

  agg.state = 'output-available';
}

/**
 * Handle tool-output-error chunk
 */
function handleOutputError(agg: AggregatedToolCall, part: MessagePart): void {
  agg.errorText = part.errorText;
  agg.state = 'output-error';
}

/**
 * Handle tool-output-denied chunk
 */
function handleOutputDenied(agg: AggregatedToolCall): void {
  agg.state = 'output-denied';
}

/**
 * Handle tool-approval-requested chunk
 */
function handleApprovalRequested(agg: AggregatedToolCall, part: MessagePart): void {
  if (part.approval) {
    agg.approval = {
      id: part.approval.id,
    };
  }
  agg.state = 'approval-requested';
}

/**
 * Handle tool-approval-responded chunk
 */
function handleApprovalResponded(agg: AggregatedToolCall, part: MessagePart): void {
  if (part.approval) {
    agg.approval = {
      id: part.approval.id,
      approved: part.approval.approved,
      reason: part.approval.reason,
    };
  }
  agg.state = 'approval-responded';
}

/**
 * Handle legacy tool part format (tool-{toolName} with state property)
 * This handles the current format used in Message.tsx
 */
function handleLegacyToolPart(agg: AggregatedToolCall, part: MessagePart): void {
  // Extract tool name from type (e.g., "tool-searchCandidates" -> "searchCandidates")
  const toolName = part.type.replace('tool-', '');
  if (toolName && !agg.toolName) {
    agg.toolName = toolName;
  }

  // Map legacy state to UIToolInvocationState
  const state = part.state as string;
  switch (state) {
    case 'input-streaming':
    case 'partial':
      agg.state = 'input-streaming';
      if (part.input !== undefined) {
        agg.input = part.input;
      }
      break;

    case 'input-available':
    case 'call':
      agg.state = 'input-available';
      agg.input = part.input;
      agg.inputComplete = true;
      break;

    case 'output-available':
    case 'result':
      agg.state = 'output-available';
      agg.input = part.input;
      agg.inputComplete = true;
      if (part.output !== undefined) {
        const preliminary = part.preliminary ?? false;
        agg.outputs.push({
          output: part.output,
          preliminary,
          timestamp: Date.now(),
        });
        if (!preliminary) {
          agg.finalOutput = part.output;
        }
      }
      break;

    case 'output-error':
    case 'error':
      agg.state = 'output-error';
      agg.input = part.input;
      agg.inputComplete = true;
      agg.errorText = part.errorText;
      break;
  }
}

/**
 * Handle unknown tool part type
 * Tries to extract useful information from any tool-prefixed part
 */
function handleUnknownToolPart(agg: AggregatedToolCall, part: MessagePart): void {
  // Extract tool name from type if not already set
  const toolName = part.type.replace('tool-', '');
  if (toolName && !agg.toolName) {
    agg.toolName = toolName;
  }

  // Try to extract input
  if (part.input !== undefined && agg.input === undefined) {
    agg.input = part.input;
    agg.inputComplete = true;
  }

  // Try to extract output
  if (part.output !== undefined) {
    const preliminary = part.preliminary ?? false;
    agg.outputs.push({
      output: part.output,
      preliminary,
      timestamp: Date.now(),
    });
    if (!preliminary) {
      agg.finalOutput = part.output;
    }
    agg.state = 'output-available';
  }

  // Handle error text
  if (part.errorText) {
    agg.errorText = part.errorText;
    agg.state = 'output-error';
  }
}

/**
 * Convert aggregated tool calls map to array sorted by first occurrence
 */
export function aggregateToolCallsToArray(parts: MessagePart[]): AggregatedToolCall[] {
  const map = aggregateToolCalls(parts);
  return Array.from(map.values());
}

/**
 * Get a single tool call by ID from parts
 */
export function getToolCallById(
  parts: MessagePart[],
  toolCallId: string
): AggregatedToolCall | undefined {
  const map = aggregateToolCalls(parts);
  return map.get(toolCallId);
}

/**
 * Check if a message has any tool parts
 */
export function hasToolParts(parts: MessagePart[]): boolean {
  return parts.some(part => part.type?.startsWith('tool-'));
}

/**
 * Get all tool call IDs from parts
 */
export function getToolCallIds(parts: MessagePart[]): string[] {
  const ids = new Set<string>();
  for (const part of parts) {
    if (part.type?.startsWith('tool-') && part.toolCallId) {
      ids.add(part.toolCallId);
    }
  }
  return Array.from(ids);
}
